import type { CoachingSession } from '@efdigital/ll-addon-data';
import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';

interface ActionRequiredDashboardRowSignature {
  Args: {
    session: CoachingSession;
    /** Person named in the row, e.g. coachee or coach display name. */
    personLabel: string;
    /** Plain-language status from `getSessionStatusDisplay`, e.g. "Awaiting your response". */
    statusLabel: string;
    /** Both dashboards link to their own "needs your attention" route, which
     *  takes no dynamic segments. */
    linkRoute: string;
  };
  Element: HTMLDivElement;
}

function sessionTypeLabel(sessionType: CoachingSession['sessionType']): string {
  if (sessionType === 'coaching') {
    return 'Coaching session';
  }

  return sessionType.replace(/-/g, ' ');
}

/**
 * Dashboard row for a session that needs action. The warning border is
 * supplementary — the warning icon, "Action required" label, and status
 * text carry the meaning for colourblind users and screen readers.
 */
const ActionRequiredDashboardRow: TOC<ActionRequiredDashboardRowSignature> =
  <template>
    {{#let (sessionTypeLabel @session.sessionType) as |typeLabel|}}
      {{! No `role="group"`/`aria-label` on the card: the stretched link below
          already announces the same sentence, and labelling the container as
          well made screen readers read the row out twice. }}
      <div
        class="card border border-warning border-2 shadow-sm rounded-3 position-relative overflow-hidden"
        data-test-action-required-row
        ...attributes
      >
        <div class="p-2 p-sm-3">
          <div class="d-flex align-items-center gap-2 gap-sm-3 min-w-0">
            <div class="flex-shrink-0 d-flex align-items-center">
              <iconify-icon
                inline
                icon="ph:warning"
                class="fs-2 text-warning"
                aria-hidden="true"
              />
            </div>

            <div class="flex-grow-1 min-w-0">
              <p class="gud-eyebrow mb-1">Action required</p>
              <h3
                class="h6 fw-bold mb-1 text-capitalize text-break"
              >{{typeLabel}}</h3>
              <p class="small mb-0 text-break">
                {{@personLabel}}
                ·
                {{@statusLabel}}
              </p>
            </div>

            <div class="flex-shrink-0">
              <iconify-icon
                inline
                icon="ph:caret-right"
                class="fs-4"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
        <LinkTo @route={{@linkRoute}} class="stretched-link">
          <span class="visually-hidden">Action required:
            {{typeLabel}},
            {{@personLabel}},
            {{@statusLabel}}. Review on Need your attention page.</span>
        </LinkTo>
      </div>
    {{/let}}
  </template>;

export default ActionRequiredDashboardRow;
